import React from 'react'
import cn from 'classnames'
import { usePlansContext, useTiersInfo, type PlansTier } from '@mc/plans-ui'
import { TrackingContainer } from '@mc/visibility'
import classes from './DesktopMatrix.module.scss'

export interface TierColumn {
  tier: PlansTier
  isSelected: boolean
  onSelect: () => void
  isPro: boolean
}

export const useTierColumns = (): TierColumn[] => {
  const { individual, duo, family, familyPlus } = useTiersInfo()
  const {
    isIndividualSelected,
    isDuoSelected,
    isFamilySelected,
    isFamilyPlusSelected,
    onClickIndividual,
    onClickDuo,
    onClickFamily,
    onClickFamilyPlus,
  } = usePlansContext()

  return [
    {
      tier: individual,
      isSelected: isIndividualSelected,
      onSelect: onClickIndividual,
      isPro: false,
    },
    {
      tier: duo,
      isSelected: isDuoSelected,
      onSelect: onClickDuo,
      isPro: false,
    },
    {
      tier: family,
      isSelected: isFamilySelected,
      onSelect: onClickFamily,
      isPro: false,
    },
    {
      tier: familyPlus,
      isSelected: isFamilyPlusSelected,
      onSelect: onClickFamilyPlus,
      isPro: true,
    },
  ]
}

export const Row = ({
  title,
  subtitle,
  onFirstVisible,
  children,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  onFirstVisible?: () => void
  children: (column: TierColumn) => React.ReactNode
}) => {
  const columns = useTierColumns()

  return (
    <TrackingContainer
      role='row'
      className={classes.row}
      onFirstVisible={onFirstVisible}
    >
      <div role='cell' className={classes.label}>
        <span className={classes.labelTitle}>{title}</span>
        {subtitle && <span className='mc-opacity--hinted'>{subtitle}</span>}
      </div>
      {columns.map((column, index) => (
        <div
          key={index}
          role='cell'
          aria-current={column.isSelected}
          className={cn(classes.cell, {
            [classes.selected]: column.isSelected,
          })}
        >
          {children(column)}
        </div>
      ))}
    </TrackingContainer>
  )
}
